/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// react
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Link,
  useParams,
  useNavigate,
  useLocation,
  useOutletContext,
  Outlet
} from 'react-router-dom';
// redux
import { useDispatch, useSelector } from 'react-redux';
import { selectReviews } from '../reviews/store/reviewsSlice';
import { navbarClose } from '../../store/BASE/navbarSlice';
// actions
import useInstance from '../../actions/useInstance';
import { getInstance } from '../../actions';
import { getReviews } from '../reviews/store/reviewsSlice';
// BASE components
import BASEPageSimple from '../../../@BASE/core/BASEPageSimple';
import BASELoading from '../../../@BASE/core/BASELoading';
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
import useThemeMediaQuery from '../../../@BASE/hooks/useThemeMediaQuery';
import NavLinkAdapter from '../../../@BASE/core/NavLinkAdapter';
import { updateNavigationItem } from '../../store/BASE/navigationSlice';
// material-ui
import { styled, useTheme, lighten } from '@mui/material/styles';

import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Hidden from '@mui/material/Hidden';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
// custom components
import PreTrialPageHeader from './PreTrialPageHeader';
import PageComments from './PageComments';
import ContentView from '../pages/instance/views/ContentView';
import PageInfo from '../pages/instance/PageInfo';
import SectionPageList from '../pages/instance/SectionPageList';
import NavigationTabination from '../../shared-components/sub-navigation/NavigationTabination';


const Root = styled(BASEPageSimple)(({ theme }) => ({
  '& .BASEPageSimple-header': {},
  '& .BASEPageSimple-toolbar': {},
  '& .BASEPageSimple-content': {},
  '& .page-header': {
    backgroundColor: theme.palette.background.qaRed,
    color: '#000000'
  },
  '& .BASEPageSimple-sidebarHeader': {},
  '& .BASEPageSimple-sidebarContent': {}
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& td, & th': {
    border: 'solid 1px #000',
    backgroundColor: 'transparent'
  }
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14
  }
}));

function PageView(props) {
  const dispatch = useDispatch();
  const location = useLocation();
  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));
  const theme = useTheme();
  const routeParams = useParams();
  const navigate = useNavigate();
  // instance hook
  const {
    instance,
    instanceDetails,
    handleInstanceAction,
    loading,
    error,
    setError
  } = useInstance('pages', routeParams.id, 'view');
  const [section, setSection] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);

  const [value, setValue] = React.useState(0);

  const [mockupImgElement, setMockupImgElement] = useState(null);
  const mockupImgRef = useCallback((node) => {
    if (node !== null) {
      setMockupImgElement(node);
    }
  }, []);

  const [mockupImg, setMockupImg] = useState({
    width: 0,
    height: 0
  });
  const [lastKnownImgSize, setLastKnownImgSize] = useState({
    width: 0,
    height: 0
  });

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  // get data from API on page load
  const getAllPages = async () => {
    try {
      const response = await dispatch(
        getInstance('sections')(instance?.section?.id)
      ).unwrap();
      setSection(response);
      setReviews(response?.lesson?.reviews);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (instance) {
      getAllPages();
      dispatch(navbarClose());
      dispatch(
        updateNavigationItem('course-component', {
          url: `courses/${instance?.section?.lesson?.course?.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('lesson-component', {
          url: `lessons/${instance?.section?.lesson?.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('section-component', {
          url: `sections/${instance?.section?.id}`,
          disabled: false
        })
      );
      dispatch(
        updateNavigationItem('page-component', {
          url: `pages/${routeParams.id}`,
          disabled: false
        })
      );
    }
  }, [dispatch, instance]);

  useEffect(() => {
    if (location.pathname.includes('/comments')) {
      setRightSidebarOpen(true);
    } else {
      setRightSidebarOpen(false);
    }
  }, [isMobile, location.pathname]);

  const debounce = (callback, delay = 200) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };
  };

  const debouncedResize = useCallback(
    debounce((width, height) => {
      setMockupImg((prev) => {
        if (prev.width !== width || prev.height !== height) {
          return { width, height };
        }
        return prev;
      });
    }, 300)
  );

  useEffect(() => {
    if (!mockupImgElement) return;

    const observer = new ResizeObserver((entries) => {
      if (!entries.length) return;

      const { width, height } = entries[0].contentRect;

      debouncedResize(width, height);
    });

    observer.observe(mockupImgElement);
    setMockupImg({
      width: mockupImgElement.clientWidth,
      height: mockupImgElement.clientHeight
    });

    return () => observer.disconnect();
  }, [mockupImgElement]);

  useEffect(() => {
    setLastKnownImgSize(mockupImg);
  }, [value]);

  if (!instance) {
    return <BASELoading />;
  }

  return (
    <Root
      content={
        <div className="w-full">
          <div className="page-header w-full">
            <PreTrialPageHeader
              instance={instance}
              details={instanceDetails}
              resource="pages"
              action="view"
              onSave={null}
              save_disabled
            />
          </div>
          <Hidden lgUp>
            <Paper
              className="flex sticky top-0 z-10 items-center w-full px-16 py-8 border-b-1 shadow-0"
              square
            >
              <IconButton
                to={`/sections/${instance?.section?.id}`}
                component={Link}
                className=""
              >
                <BASESvgIcon>
                  {theme.direction === 'ltr'
                    ? 'heroicons-outline:arrow-sm-left'
                    : 'heroicons-outline:arrow-sm-right'}
                </BASESvgIcon>
              </IconButton>

              <Typography className="text-13 font-medium tracking-tight mx-10">
                Back to section
              </Typography>
            </Paper>
          </Hidden>

          <div className="flex justify-center w-full">
            <Paper className="w-full rounded-0 shadow overflow-hidden">
              <div className="w-full max-w-full" dir={theme.direction}>
                <div className="w-full">
                  <div className="flex flex-col p-32 sm:p-24">
                    <PageComments instance={instance} reviews={reviews} collection='pre_trial_comments' />
                  </div>
                </div>
              </div>
            </Paper>
          </div>
        </div>
      }
      rightSidebarContent={
        <div
          className={
            isMobile ? 'relative' : 'fixed top-0 h-screen overflow-y-auto'
          }
          style={isMobile ? { width: '100%' } : { width: 384 }}
        >
          <Outlet context={{ instance, reviews }} />
        </div>
      }
      rightSidebarOpen={rightSidebarOpen}
      rightSidebarOnClose={() => setRightSidebarOpen(false)}
      rightSidebarWidth={384}
      scroll={isMobile ? 'normal' : 'content'}
    />
  );
}

export default PageView;
